function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    // In PDP, the same section is rendered for both the main product view and the Quickview.
    // This causes labels and inputs to share the same IDs, so interactions in Quickview (like changing variants)
    // unintentionally affect the main PDP elements. To prevent conflicts, we manually adjust the IDs and "for"
    // attributes in Quickview by appending unique values.

    const quickviewModal = oldNode.parentNode?.closest("quickview-modal") || null
    
    if (newNode.tagName === "VARIANT-SELECTS" && quickviewModal) {
      if (quickviewModal.hasAttribute("data-modify")) {

        const labels = newNode.querySelectorAll(".product-form__input label") || []

        if (labels.length > 0) {

          labels.forEach((label, index) => {

            const value = label.getAttribute("for")
            const updatedValue =`${value}-${index}`

            label.setAttribute("for", updatedValue)
            const input = newNode.querySelector(`input[id="${value}"]`)

            if (input) input.id = updatedValue
          })
        }
      }
    }

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  const mainFiltersContainer = summary.closest("#main-collection-filters")
  const searchFiltersContainer = summary.closest("#main-search-filters")
  const filters = [mainFiltersContainer, searchFiltersContainer]

  summary.addEventListener("click", (event) => {
    const hasIcon = summary.querySelector(".icon-close") || summary.querySelector(".icon-hamburger")
    const isOpen = !event.currentTarget.closest("details")?.hasAttribute("open") || false

    if (summary.closest("header-drawer") && hasIcon) {
      const icons = document.querySelector('.header__icons.header__icons--localization')
      const headerHeading = document.querySelector(".header__heading")

      if (icons) icons.classList.toggle("hidden", isOpen)
      if (headerHeading) headerHeading.classList.toggle("mobile__view", isOpen)
    }
    event.currentTarget.setAttribute("aria-expanded", isOpen);
    
    filters.forEach(filter => {
      if (filter && window.matchMedia("(max-width: 990px)").matches) {
        filter.classList.add('adjust-z-index');
      }
    })
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

window.updateButtonStates = function (quantityInput, minusIcon, plusIcon, updateMainQtyInput) {
  const minValue = Number(quantityInput.min);
  const maxValue = Number(quantityInput.max);
  const currentValue = Number(quantityInput.value);

  if (minValue && currentValue <= minValue) {
    minusIcon.setAttribute("disabled", true);
  } else {
    minusIcon.removeAttribute("disabled");
  }

  if (maxValue && currentValue >= maxValue) {
    plusIcon.setAttribute("disabled", true);
  } else {
    plusIcon.removeAttribute("disabled");
  }

  if (updateMainQtyInput) {
    const mainQuantityInput = document.querySelector("product-info .quantity__input");
    mainQuantityInput.value = currentValue;
  }
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );

    this.elementToSync = null

    if (this.closest(".product")) {
      this.elementToSync = ".sticky-cart-wrapper .quantity__input[name='quantity']"
    } else if (this.closest(".sticky-cart-wrapper")) {
      this.elementToSync = ".product .quantity__input[name='quantity']"
    }

    this.elementToSync = document.querySelector(this.elementToSync) || null
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const stickCartForm = document.querySelector('.sticky-cart product-form')
    const productForm = document.querySelector('.default_form product-form')

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    [stickCartForm, productForm].forEach(ele => {
      if (ele) ele.setAttribute("data-current-qty", this.input.value);
    })

    this.input.value = Math.max(1, Number(this.input.value) || 1)

    // Sync another quantity selector on PDP (Sync the QTY selectors in product and sticky container)
    if (this.elementToSync) this.elementToSync.value = this.input.value

    const quantityInput = document.querySelector(".customization-popup .quantity__input");
    const plusIcon = document.querySelector(`.customization-popup .quantity__button[name="plus"]`);
    const minusIcon = document.querySelector(`.customization-popup .quantity__button[name="minus"]`);

    if (quantityInput) {
      quantityInput.value = this.input.value
      window.updateButtonStates(quantityInput, minusIcon, plusIcon)
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }

    this.input.value = Math.max(1, Number(this.input.value) || 1)

    // Sync another quantity selector on PDP (Sync the QTY selectors in product and sticky container)
    if (this.elementToSync) this.elementToSync.value = this.input.value
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

// var priorityKeywords = [
//   'cart-drawer',
//   'product-form',
//   'cart',
//   'global',        // ← fetchConfig is usually defined here
// ];

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.mainFiltersContainer = document.querySelector("#main-collection-filters")
    this.searchFiltersContainer = document.querySelector("#main-search-filters")
    this.filters = [this.mainFiltersContainer, this.searchFiltersContainer]

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary"),
      )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".menu-drawer__submenu.has-submenu")
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("child__menu-parent");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus,
          );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
      "overflow-hidden"
    );

    this.filters.forEach(filter => {
      if (filter) filter.classList.remove("adjust-z-index")
    })

    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement, event.currentTarget.classList.contains("secondary"));
  }

  closeSubmenu(detailsElement, secondaryCloseBtn) {
    const overlay = document.querySelector(".background-overlay");
    const filters = [document.querySelector("#main-collection-filters"), document.querySelector("#main-search-filters")];
    const parentMenuElement = detailsElement.closest(".menu-drawer__submenu.has-submenu");
    parentMenuElement && parentMenuElement.classList.remove("child__menu-parent");
    detailsElement.classList.remove("menu-opening");
    if (overlay) overlay.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
    if (!secondaryCloseBtn) filters.forEach(filter => { if (filter) filter.classList.remove("adjust-z-index") });
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
        this.closest(".header-wrapper").classList.contains(
          "header-wrapper--border-bottom",
        )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add("menu-open");

    // Update cart icon bubble inside custom-header-drawer
    const drawerCartIconBubble = document.querySelector(".menu-drawer__utility-links .cart-count-bubble")
    const mainHeaderCartIconBubble = document.querySelector("#cart-icon-bubble .cart-count-bubble")

    if (drawerCartIconBubble && mainHeaderCartIconBubble) {
      drawerCartIconBubble.innerHTML = mainHeaderCartIconBubble.innerHTML
    }

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }

    const saveButton = document.querySelector(".save-button")

    if (saveButton) {
      const spinner = saveButton.querySelector(".loading__spinner")

      const toggleLoadingState = (isLoading) => {
        saveButton.classList.toggle("loading", isLoading)
        spinner?.classList.toggle("hidden", !isLoading)
      }

      saveButton.addEventListener("click", () => {
        toggleLoadingState(true)

        setTimeout(() => {
          toggleLoadingState(false)
          this.hide()
        }, 1200)
      })
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    if (this.closest(".shopify-section")) {
      this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    }
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container",
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
      this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    // Use total slides and Current page to calculate the progress bar width
    const progress = ((100 / this.totalPages) * this.currentPage) || 100
    this.style.setProperty("--progress", `${progress}%`)

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active",
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true,
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    if (currentSlide) currentSlide.classList.add(`${animationClassOut}-${direction}`);
    if (nextSlide) nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      if (currentSlide) currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      if (nextSlide) nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      // Update the selected swatch in variant picker popup
      const swatchValue = target.value
      const previousSelectedSwatch = document.querySelectorAll('.swatch_label.selected')
      const currentSelectedSwatch = document.querySelector(`.variant_picker_popup .swatch_label[title="${swatchValue}"]`)
      const currentSwatchInCustomization = document.querySelector(`.customization-popup .swatch_label[title="${swatchValue}"]`)

      if (previousSelectedSwatch.length > 0) previousSelectedSwatch.forEach(swatch => swatch.classList.remove("selected"))
      if (currentSelectedSwatch) currentSelectedSwatch.classList.add("selected")
      if (currentSwatchInCustomization) currentSwatchInCustomization.classList.add("selected")

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      const popupSwatchLabel = document.querySelector(".variant_picker_popup .product-form__input--swatch [data-selected-value]");
      const labels = [selectedSwatchValue, popupSwatchLabel]

      this.updateSwatchlable(labels, value)
    }
  }

  updateSwatchlable(labels, value) {
    labels.forEach(label => {
      if (label) label.innerHTML = value
    })
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    if (this.closest('.bundle')) {
      return Array.from(
        this.querySelectorAll("select option[selected], .bundle fieldset input:checked"),
      ).map(({ dataset }) => dataset.optionValueId);
    } else {
      return Array.from(
        this.querySelectorAll("select option[selected], fieldset input:checked"),
      ).map(({ dataset }) => dataset.optionValueId);
    }
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;

          const cardImages = this.querySelectorAll(".card__media img");
          window.showImageLoading(cardImages);

          this.classList.add("product-recommendations--loaded");

          this.dispatchEvent(
            new CustomEvent("recommendations:loaded", { bubbles: true })
          );
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}


// ----- STICKY ADD TO CART -----
function handleClick(buttonSelector, targetSelector, bundle) {
  const button = document.querySelector(buttonSelector)

  if (button) {
    button.addEventListener("click", () => {
      if (bundle) button.setAttribute("data-bundle", "true")

      button.classList.add("loading")
      const loader = button.querySelector(".loading__spinner")
      if (loader) loader.classList.remove("hidden")

      const targetButton = document.querySelector(targetSelector)
      
      if (targetButton) {

        // Set bundle's discount value to product form which will be used to apply discount to the bundle
        if (button.hasAttribute("data-bundle-name")) {
          const productForm = targetButton.closest("product-form")
          productForm.setAttribute("data-bundle-name", button.getAttribute("data-bundle-name"))
        }

        targetButton.click() // Trigger the default button
      } else {
        console.warn(`Target button ${targetSelector} not found`)
        // Remove loading state if target button not found
        button.classList.remove("loading")
        if (loader) loader.classList.add("hidden")
      }
    })
  }
}

function initStickyButtons() {
  handleClick('.sticky-atc', '.default_form .product-form__submit', false)
  handleClick('.sticky-buynow', '.shopify-payment-button__button', false)
  handleClick('.get-the-look__cart-button', '.bundle_form .product-form__submit', true)
  handleClick('.make-a-bundle .bundle-cart-button', '.default_form .product-form__submit', true)
}

// Initialize Sticky Buttons
initStickyButtons()

const bundleAddToCartButtons = document.querySelectorAll("#cart-button")

if (bundleAddToCartButtons.length > 0) {
  bundleAddToCartButtons.forEach((button, index) => {
    button.addEventListener("click", () => {
      const bundleForm = document.querySelectorAll(".bundle_form .product-form__submit")[index]
      bundleForm.click() // Trigger the form
    })
  })
}


// Observe and show Sticky add to cart when the default add to cart left the viewport
const targetElement = document.querySelector('.product-form__buttons');
const stickyCartElement = document.querySelector('.sticky-cart-wrapper');

if (targetElement && stickyCartElement) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      console.log('isIntersecting:', entry.isIntersecting);

      if (entry.isIntersecting) {
        stickyCartElement.classList.remove('active');
      } else {
        stickyCartElement.classList.add('active');
      }
    });
  }, {
    threshold: 0
  });

  observer.observe(targetElement);
}

// Gift wrap popup
const modal = document.querySelector(".product-popup-modal__content.gift_wrapper_modal")

if (modal) {
  const closeButton = modal.querySelector(".product-popup-modal__toggle")
  if (closeButton) closeButton.classList.add("tw-hidden")
}

// VIEW MORE..
class ViewMoreButton extends HTMLElement {
  constructor() {
    super()

  }

  // Called when the element is connected to the document
  connectedCallback() {
    this.bindEvents();
  }

  // Bind events to the custom element
  bindEvents() {
    this.addEventListener('click', () => this.handleViewMoreClick())
  }

  // Handle the "View More" button click
  handleViewMoreClick() {
    const popup = document.querySelector('.variant_picker_popup')
    if (popup) {
      this.updateSwatchesInPopup(popup)
      this.showPopup(popup)
    }
  }

  updateSwatchesInPopup(popup) {
    const disabledInputs = document.querySelectorAll(".product__info-wrapper .swatch-input__input.visually-disabled")

    if (disabledInputs.length > 0) {
      const inputsInPopup = popup.querySelectorAll(".swatch-input__input")
      const disabledValues = Array.from(disabledInputs).map(input => input.value)
      const selectedSwatchLabel = document.querySelector(".product__info-container .product-form__input--swatch [data-selected-value]")
      const popupSwatchLabel = popup.querySelector(".product-form__input--swatch [data-selected-value]")
      const selectedSwatchInput = document.querySelector(".swatch-input__input:checked")

      if (selectedSwatchLabel && popupSwatchLabel) {
        popupSwatchLabel.textContent = selectedSwatchLabel.textContent
      }

      if (inputsInPopup.length) {
        inputsInPopup.forEach(input => {
          const label = input.nextElementSibling
          if (!label) return

          if (selectedSwatchInput && selectedSwatchInput.value === input.value) {
            label.classList.add("selected")
          } else {
            label.classList.remove("selected")
          }

          input.classList.toggle("visually-disabled", disabledValues.includes(input.value))
        })
      }
    }
  }

  // Show the popup and attach relevant event listeners
  showPopup(popup) {
    const closeIcon = popup.querySelector('.svg-wrapper.close')
    const chooseVariantBtn = popup.querySelector('.choose-variant')
    const overlay = document.querySelector(".background-overlay")

    // Show the popup and overlay
    popup.classList.remove('hide-visibility')
    overlay.classList.remove('hidden')
    document.body.classList.add("overflow-hidden")

    // Bind close event for the popup
    closeIcon?.addEventListener('click', () => this.closePopup(popup, overlay))

    // Handle loading spinner for the choose variant button
    this.loadingSpinner(chooseVariantBtn, popup, overlay)
  }

  // Close the popup
  closePopup(popup, overlay) {
    popup.classList.add('hide-visibility')
    overlay.classList.add('hidden')
    document.body.classList.remove("overflow-hidden")
  }

  // Toggle the loading spinner state
  loadingSpinner(button, popup, overlay) {
    const spinner = button.querySelector('.loading__spinner');

    const toggleLoadingState = (isLoading) => {
      button.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    };

    // Attach event listener to the button
    button.addEventListener('click', () => {
      toggleLoadingState(true);

      // Simulate loading process, then hide the popup
      setTimeout(() => {
        toggleLoadingState(false);
        this.closePopup(popup, overlay);
      }, 1200);
    });
  }
}

// Define the custom element
customElements.define('view-more', ViewMoreButton);


// Select the selected swatch in Product info
function updateSelectedSwatch(container = document) {
  const selectedVariantInput = container.querySelector(".variant_picker_popup .swatch-input__input:checked")

  if (selectedVariantInput) {
    const selectedSwatchValue = selectedVariantInput.value

    // Find the corresponding swatch input in the product info section based on the selected value
    const productSwatchInput = container.querySelector(`product-info .swatch-input__input[value="${selectedSwatchValue}"]`)

    if (productSwatchInput) {
      const swatch = productSwatchInput.nextElementSibling
      if (swatch && swatch.classList.contains("swatch_label")) swatch.classList.add("selected")
    }
  }
}

// Select the selected Variant Pills in product info
function updateSelectedVariantPill(container = document) {
  const selectedInputs = container.querySelectorAll(".product-form__input--pill input") || []
  const filteredInputs =Array.from(selectedInputs).filter(input => input.hasAttribute("checked"))

  if (filteredInputs.length > 0) {
    filteredInputs.forEach(ele => {

      const label = ele.nextElementSibling
      if (label) label.classList.add("selected")
    })
  }
}

updateSelectedSwatch()
updateSelectedVariantPill()


// CARD PRODUCT VARIANT CHANGE...
class CardSwatches extends HTMLElement {
  constructor() {
    super()

    this.swatches = []
    this.mainImage = null
    this.variantImage = ''

    this.handleSwatchClick = this.handleSwatchClick.bind(this)
    this.handleViewMore = this.handleViewMore.bind(this)
  }

  connectedCallback() {
    this.swatches = this.querySelectorAll('.swatch_wrapper')
    this.container = this.closest('.card-wrapper')
    this.bundlesContainer = this.closest(".bundle__products-container")

    if (this.swatches.length > 0) {
      this.swatches.forEach(swatch => {
        swatch.addEventListener("click", this.handleSwatchClick)
      })
    }

    this.viewMoreButton = this.querySelector(".view-more-swatches")
    
    if (this.viewMoreButton) {
      this.viewMoreButton.addEventListener("click", this.handleViewMore)
    }

    // Update the main bundle cart button based on selected swatches
    this.updateBundleCartButton()
  }

  handleViewMore(event) {
    const otherPopups = document.querySelectorAll(".card-swatches-popup") || []
    const backgroundOverlay = document.querySelector(".background-overlay")

    this.toggleClasses(otherPopups, "visibility-hidden", true)
    this.parentContainer = event.target.closest(".card_variant_swatches")
    this.popup = this.parentContainer?.nextElementSibling

    if (this.popup && backgroundOverlay) {

      const closeButton = this.popup.querySelector(".close")

      this.popup.classList.remove("visibility-hidden")
      backgroundOverlay.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")

      if (closeButton) { 
        closeButton.addEventListener("click", () => {
          this.popup.classList.add("visibility-hidden")
          backgroundOverlay.classList.add("hidden")
          document.body.classList.remove("overflow-hidden")
        })
      }
    }
  }

  handleSwatchClick(event) {

    if (event.target.closest(".swatch_wrapper")) {

      const swatch = event.target.closest(".swatch_wrapper")
      
      this.toggleClasses(this.swatches, "selected", false)
      this.toggleClasses(this.swatches, "order-1", false)
      swatch.classList.add("selected")

      this.mainImage = this.container.querySelector(".card__inner .card__media img")
      this.variantImage = swatch.getAttribute("data-image")

      // Update the main image if variant image exists
      if (this.variantImage && this.mainImage) {
        this.mainImage.src = this.variantImage
        this.mainImage.srcset = this.variantImage

        const variantId = parseInt(swatch.getAttribute("data-variant-id"))

        if (!isNaN(variantId)) {
          this.cardFormInput = this.container.querySelector(".quick-add form input.product-variant-id")

          if (this.cardFormInput) {

            const swatchesToSelect = this.container.querySelectorAll(`.swatch_wrapper[data-variant-id="${variantId}"]`)
            const selectedSwatches = this.container.querySelectorAll(`.swatch_wrapper.selected`)

            this.toggleClasses(selectedSwatches, "selected", false)
            this.toggleClasses(swatchesToSelect, "selected", true)
            
            this.cardFormInput.value = variantId

            if (this.container.classList.contains("main_bundle_card") && this.bundlesContainer) {
              this.mainFormInput = this.bundlesContainer.querySelector('.bundle_form form .product-variant-id')

              if (this.mainFormInput) {
                this.mainFormInput.value = variantId
              }
            } else {
              this.container.setAttribute("data-variant-id", variantId)
            }
          }
        }

        // Toggle the inner text of the cart buttons based on the selected swatch
        const addToCartButton = this.container.querySelector(".card__content .quick-add__submit")
        const isAvailable = swatch.getAttribute("data-availability")

        if (addToCartButton) this.toggleInnerText(isAvailable, addToCartButton)

        // If the selected swatch is from popup and If the section is get the look, update the related swatch in product card
        const cardSwatchPopup = swatch.closest(".card-swatches-popup")
        const parentContainer = swatch.closest(".card__content")

        if (cardSwatchPopup && parentContainer) {
          const relatedSwatch = parentContainer.querySelector(`.card_variant_swatches .swatch_wrapper[data-variant-id="${variantId}"]`)
          
          const swatchWrappers = parentContainer.querySelectorAll(".card_variant_swatches .swatch_wrapper") || []

          if (swatchWrappers.length > 4) {
                        
            // Hide all swatches after the 4th one
            swatchWrappers.forEach((ele, index) => {
                if (index > 3) {
                  ele.classList.add("tw-hidden")
                } else {
                  ele.classList.remove("tw-hidden")
                }
            })

            if (relatedSwatch) {
              const selectedIndex = Array.from(swatchWrappers).indexOf(relatedSwatch)
              const indexToHide = selectedIndex > 3 ? 3 : 4

              swatchWrappers[indexToHide].classList.add("tw-hidden") // Hide the 5th swatch

              // Show related swatch in first position and Select the related swatch
              relatedSwatch.classList.add("selected", "order-1")
              relatedSwatch.classList.remove("tw-hidden")
            }
          }
        }
      }

      // Update the main bundle cart button based on selected swatches
      this.updateBundleCartButton()
    }
  }

  updateBundleCartButton() {

    if ( this.bundlesContainer) {
      const bundleCartButton = this.bundlesContainer.querySelector(".get-the-look__cart-button")

      if (!bundleCartButton) {
        console.warn("Bundle cart button not found")
        return
      }

      const textContent = bundleCartButton.getAttribute("data-text-content")
      const swatchWrappers = this.bundlesContainer.querySelectorAll(".card_variant_swatches .swatch_wrapper") || []

      if (swatchWrappers.length > 0) {
        const filteredSwatches = Array.from(swatchWrappers).filter(swatch => swatch.classList.contains("selected") && swatch.getAttribute("data-availability") === "false")

        if (filteredSwatches.length > 0) {
          this.toggleInnerText("false", bundleCartButton)
        } else {
          this.toggleInnerText("true", bundleCartButton, textContent)
        }
      }
    }
  }

  toggleInnerText(isAvailable, cartButton, value) {

    const cartButtonText = cartButton.querySelector("span")
    const textContent = value || window.variantStrings.addToCart

    if (cartButtonText) {
      cartButtonText.textContent = isAvailable === "false" 
        ? window.variantStrings.soldOut
        : textContent;
    }

    cartButton.disabled = (isAvailable === "false")
  }

  toggleClasses(elements, className, value) {
    if (elements.length > 0) {
      elements.forEach(ele => ele.classList.toggle(className, value))
    }
  }

  disconnectedCallback() {
    // Remove the event listeners when the element is removed from the DOM
    if (this.swatches.length > 0) {
      this.swatches.forEach(swatch => {
        swatch.removeEventListener("click", this.handleSwatchClick)
      })
    }
  }
}

customElements.define("card-swatches", CardSwatches)

// TOGGLE SIZE GUIDE..
const sizeGuide = document.querySelector("#size_guide")
const sizeGuidePopup = document.querySelector(".size_guide_popup")
const overlay = document.querySelector(".background-overlay")

if (sizeGuide && sizeGuidePopup && overlay) {
  const closeSizeGuide = sizeGuidePopup.querySelector(".close")

  sizeGuide.addEventListener("click", () => toggleSizeGuide(sizeGuidePopup, overlay, false))
  closeSizeGuide.addEventListener("click", () => toggleSizeGuide(sizeGuidePopup, overlay, true))
}

// Show and hide Size guide popup
function toggleSizeGuide(ele1, ele2, value){
  ele1.classList.toggle("visually-hidden", value)
  ele2.classList.toggle("hidden", value)
  document.body.classList.toggle("overflow-hidden", !value) // Control background scroll
}

// SET IDs FOR APPLY FILTER BUTTON IN SUBFILTERS...
window.setInputIDs = function () {
  const filterElements = document.querySelectorAll(".subfilters .filter_element") || []
  if (filterElements.length === 0) return

  filterElements.forEach(filter => {
    const listElements = filter.querySelectorAll("li.subfilter-facets__item") || []
    const labels = filter.querySelectorAll(".facets__label") || []

    if (listElements.length == 0) return
    if (labels.length > 0) labels.forEach(ele => ele.classList.remove("active"))

    listElements.forEach(list => {
      list.addEventListener("click", (e) => {
        e.preventDefault()

        if (e.target.closest(".subfilter-facets__item")) {

          const label = list.querySelector(".facets__label")
          const applyFilter = filter.querySelector("label.apply_filter")

          if (label && !label.classList.contains("disabled")) {

            const input = label?.querySelector("input")

            if (label.classList.contains("price-filter")) {

              const fromValue = label.getAttribute("data-price-gte")
              const toValue = label.getAttribute("data-price-lte")
              const priceRangeFacets = document.querySelector(".disclosure-has-popup.facets__disclosure .facets__price")
              if (!priceRangeFacets || !fromValue || !toValue) return

              const fromInput = priceRangeFacets.querySelector("#Filter-Price-GTE")
              const toInput = priceRangeFacets.querySelector("#Filter-Price-LTE")

              labels.forEach(ele => ele.classList.remove("active"))
              label.classList.add("active")

              if (fromInput && toInput && applyFilter) {

                fromInput.value = fromValue
                toInput.value = toValue

                applyFilter.addEventListener("click", () => {

                  const textContent = applyFilter.querySelector("span")
                  const loader = applyFilter.querySelector(".loading__spinner")

                  if (textContent && loader) {
                    applyFilter.classList.add("loading")
                    textContent.classList.add("hidden")
                    loader.classList.remove("hidden")
                  }

                  const form = priceRangeFacets.closest("form")

                  if (form) {
                    form.dispatchEvent(new Event("input")) // Dispatch the input event of the form element to trigget the filter action
                  }
                })
              }
            } else if (input && !input.checked) {
                labels.forEach(ele => ele.classList.remove("active"))
                label.classList.add("active")

                const inputId = input.id

                if (applyFilter) applyFilter.setAttribute("for", inputId)        
            }
          }
        }
      })
    })
  })
}

window.renderBannersAndSubfilters = function () {
  const container = document.querySelector("ul#product-grid");

  if (!container) return

  const products = container.querySelectorAll("li.grid__item")
  const showSubBanners = container ? container.getAttribute("data-show-sub-banners") : null;
  const showSubFilters = container ? container.getAttribute("data-show-sub-filters") : null;

  // Media query listeners for mobile and desktop breakpoints
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const desktopQuery = window.matchMedia("(min-width: 750px)");

  if (products.length > 0) {
    checkScreenSize(mobileQuery, "mobile");
    checkScreenSize(desktopQuery, "desktop");
  }

  // CHECK SCREEN SIZE AND EXECUTE LOGIC.....
  function checkScreenSize(screenQuery, screenConfig) {

    if (screenQuery.matches) {

      const viewPort = screenConfig === "mobile" ? "mobile" : "desktop";

      if (showSubBanners === "true") { // EXTRACT IMAGE URLS, BANNER INTERVAL AND PREVIOUS IMAGE INDEX.....
        getValues("image", `data-banner-interval-${viewPort}`, `data-${viewPort}-banners`);
      }

      if (showSubFilters === "true") { // EXTRACT SUB-FILTERS INTERVAL AND PREVIOUS FILTER INDEX.....
        getValues("filter", `data-sub-filters-interval-${viewPort}`);
      }
    }
  }

  function getValues(type, attr1, attr2) {
    const interval = parseInt(container.getAttribute(attr1)) || 8;
    const element = attr2 ? container.getAttribute(attr2) : container.querySelector(".subfilters")

    if (isNaN(interval) || !element) return;

    if (type === "image") {
      renderBannerImages(element, interval);
    } else {
      renderSubFilters(element, interval);
    }
  }

  // RENDER BANNER IMAGES.....
  function renderBannerImages(banners, interval) {
    const bannerArray = banners.split(",");

    if (bannerArray.length === 0) return;

    let currIndex = 0;

    products.forEach((product, index) => {

      if ((index + 1) % interval === 0) {
        container.setAttribute('data-prev-image-index', currIndex);

        const imageElement = document.createElement('img');
        imageElement.classList.add("tw-w-full", "middle-banners");
        imageElement.src = bannerArray[currIndex];
        product.insertAdjacentElement('afterend', imageElement);

        currIndex = (currIndex + 1) % bannerArray.length;
      }
    });
  }

  // RENDER SUB-FILTERS.....
  function renderSubFilters(subfilters, interval) {
    const filters = subfilters.querySelectorAll(".filter_element");

    if (filters.length === 0) return;

    let currIndex = 0;

    products.forEach((product, index) => {

      if ((index + 1) % interval === 0) {
        container.setAttribute('data-prev-filter-index', currIndex);

        const newElement = subfilters.cloneNode(true);
        const filterElements = newElement.querySelectorAll(".filter_element");

        filterElements.forEach(ele => ele.classList.add("hidden"));
        newElement.classList.remove("hidden");
        filterElements[currIndex].classList.remove("hidden");

        product.insertAdjacentElement('afterend', newElement);

        currIndex = (currIndex + 1) % filters.length;
      }
    });

    // Set eventlisteners to labels and pass the input ids to the apply filter
    window.setInputIDs()
  }
};

document.addEventListener("DOMContentLoaded", function () {
  window.renderBannersAndSubfilters();
});

// CHANGE PRODUCT'S INFO BASED ON VARIANT.....
window.updateProductInfo = function () {
  const noScript = document.querySelector("#variantMetaData")

  if (!noScript) {
    console.warn("Variant metadata not found")
    return
  }

  const noScriptData = noScript.innerHTML

  let htmlElement;
  try {
    htmlElement = new DOMParser().parseFromString(noScriptData, "text/html")
  } catch (error) {
    console.error("Error parsing variant metadata:", error)
    return
  }

  const variantMetaDataElements = htmlElement.querySelectorAll(".variant-meta-data") || []
  const productTitle = document.querySelector(".product__title h1")
  const productTitleInVariantPickerPopup = document.querySelector(".product_content .product_title")
  const stickyCartTitle = document.querySelector(".sticky-prod-title")
  const defaultTitle = productTitle?.getAttribute("data-title")
  const accordionsTitles = document.querySelectorAll(".product__accordion .summary__title") || []

  if (variantMetaDataElements.length == 0) return

  const url = window.location.href

  if (url.includes("variant=")) {

    let variantIdInURL;
    try {
      variantIdInURL = parseInt(url.split("variant=")[1].split("&")[0]);
    } catch (error) {
      console.error("Error parsing variant ID from URL:", error);
      return;
    }
    
    variantMetaDataElements.forEach(element => {
      const variantJson = element.getAttribute('data-variant-data')

      let variantMetaData;
      try {
        variantMetaData = JSON.parse(variantJson)
      } catch (error) {
        console.error("Error parsing variant data:", error)
        return
      }
      const variantID = parseInt(variantMetaData.id)
      const title = variantMetaData.title ? variantMetaData.title : defaultTitle

      if (!isNaN(variantID) && !isNaN(variantIdInURL) && variantIdInURL === variantID) {
        productTitle.innerHTML = title
        if (productTitleInVariantPickerPopup) productTitleInVariantPickerPopup.innerHTML = title
        if (stickyCartTitle) stickyCartTitle.innerHTML = title
        changeAccordionContent(accordionsTitles, variantMetaData)
        return // To break this function
      }
    })
  } else {
    productTitle.innerHTML = defaultTitle
    if (productTitleInVariantPickerPopup) productTitleInVariantPickerPopup.innerHTML = defaultTitle
    if (stickyCartTitle) stickyCartTitle.innerHTML = defaultTitle
    changeAccordionContent(accordionsTitles)
  }

  function changeAccordionContent(accordionsTitles, metaData) {

    if (accordionsTitles.length === 0) return
  
    // Mapping of titleData keywords to corresponding metaData properties
    const contentMap = {
      description: 'description',
      manufacture: 'manufacture_details',
      care: 'care_guide',
      details: 'product_details',
      composition: 'composition'
    }
  
    accordionsTitles.forEach(title => {
      const titleData = title.getAttribute("data-summary-title")
      const defaultSummary = title.getAttribute("data-summary-content")
  
      // Loop through the contentMap to check if titleData matches
      for (const key in contentMap) {

        // Skip the Manufacture details as it has "details" in it..
        if (titleData.includes(key)) {
          const summaryContent = title.closest("details")?.querySelector(".accordion__content")

          if (summaryContent) {
            summaryContent.innerHTML = metaData && metaData[contentMap[key]] != "" ? metaData[contentMap[key]] : defaultSummary
          }
          break
        }
      }
    })
  }
  
};

window.updateProductInfo()


// OPEN FIRST ACCORDION IN PRODUCT PAGE.....
// const productAccordions = document.querySelectorAll('.product__accordion details') || []
// if (productAccordions.length > 0) {
//   productAccordions[0].setAttribute("open", "true")
//   const summary = productAccordions[0].querySelector("summary")
//   if (summary) summary.setAttribute("aria-expanded", "true")
// }


// MEGAMENU HOVER INTERACTION.....
function initMegamenu() {
  const header = document.querySelector(".header__inline-menu")
  if (!header) {
    console.warn("Header not found")
    return
  }

  const summaryElements = header.querySelectorAll(".level--1 summary.header__menu-item.list-menu__item") || []

  if (summaryElements.length === 0) {
    console.warn("Summary elements not found")
    return
  }

  summaryElements.forEach(summary => {

    const detailsTag = summary.parentElement

    if (!detailsTag) {
      console.warn("Details tag not found")
    }

    summary.addEventListener("mouseenter", function () {
      summaryElements.forEach(ele => ele.parentElement?.removeAttribute("open"))
      detailsTag.setAttribute("open", "true")
    })

    if (detailsTag) {
      const content = detailsTag.querySelector('.mega-menu__content')

      if (content) {
        const closestDetails = content.closest("details.level--1")

        content.addEventListener("mouseenter", function () {
          closestDetails?.setAttribute("open", "true")
        })

        content.addEventListener("mouseleave", function () {
          closestDetails?.removeAttribute("open")
        })
      }
    }
  })
}

initMegamenu() // Initialize Mega menu functions...

// SHOW GIFT MESSAGE...
const message = document.querySelector(".product-popup-modal__opener .gift_message_info")
const inputBox = document.querySelector(".product-popup-modal__opener .gift_input")

if (message && inputBox) {
  inputBox.addEventListener("change", () => {

    if (inputBox.checked) {
      message.classList.add("active")
      setTimeout(() => message.classList.remove("active"), 2000)
    }
  })
}

// LOADING ANIMATION FOR CARD MEDIA
const cardImages = document.querySelectorAll(".card__media img.loading")

window.showImageLoading = function showImageLoading(cardImages) {

  if (cardImages.length === 0) {
    console.warn("No card images found")
    return
  }

  cardImages.forEach(image => {
    if (image.complete && image.naturalHeight !== 0) {
      removeLoading(image) // For Image already loaded (cached)
    } else {
      // If not Wait for image to load
      image.addEventListener("load", () => removeLoading(image))
    }
  })
}

// Delay removal of the loading class to allow the shimmer effect to complete smoothly
function removeLoading(image) {
  setTimeout(() => {
    image.classList.remove("loading")
  }, 1500)
}

document.addEventListener("DOMContentLoaded", () => {
  window.showImageLoading(cardImages) // Call the function to show image loading
}) // Ensure images are loaded after DOM content is fully loaded


// Quickview
class QuickView extends HTMLElement {
  constructor() {
    super()

    this.url = this.dataset.productUrl
    this.quickviewModalWrapper = document.querySelector(".quickview-modal__wrapper")
    this.quickviewModal = this.quickviewModalWrapper?.querySelector("quickview-modal")
    this.closeModalButton = this.quickviewModalWrapper?.querySelector(".close-modal")
    this.backgroundOverlay = document.querySelector(".background-overlay")
    this.addEventListener("click", () => this.renderSection())
  }

  async renderSection() {

    try {
      const response = await fetch(this.url)
      const data = await response.text()
      const parsedData = new DOMParser().parseFromString(data, "text/html")
      const section = parsedData.querySelector("section.MainProductSection")

      // In PDP, the same section is rendered for both the main product view and the Quickview.
      // This causes labels and inputs to share the same IDs, so interactions in Quickview (like changing variants)
      // unintentionally affect the main PDP elements. To prevent conflicts, we manually adjust the IDs and "for"
      // attributes in Quickview by appending unique values.

      if (this.quickviewModal && section && this.backgroundOverlay) {

        const content = this.quickviewModal.querySelector(".quickview-content")

        content.innerHTML = section.innerHTML

        if (this.quickviewModal.hasAttribute("data-modify")) {
          const variantSelects = this.quickviewModal.querySelector(".main__product-section variant-selects") || null

          if (variantSelects) {
            const labels = variantSelects.querySelectorAll(".product-form__input label")

            labels.forEach((label, index) => {
              const value = label.getAttribute("for")
              const updatedValue =`${value}-${index}`

              label.setAttribute("for", updatedValue)
              const input = variantSelects.querySelector(`input[id="${value}"]`)

              if (input) input.id = updatedValue
            })

            // Update selected swatches and pills in quickview initially  
            updateSelectedSwatch(this.quickviewModal)
            updateSelectedVariantPill(this.quickviewModal)
          }
        }

        // Share button to copy product url
        const shareButton = this.quickviewModal.querySelector(".share-button__copy")

        if (shareButton) {
          shareButton.addEventListener("click", async () => {
            try {
              await navigator.clipboard.writeText(window.location.origin + this.url)
            } catch (err) {
              console.error("Failed to copy link:", err)
            }

            setTimeout(() => {
              shareButton.closest("details")?.removeAttribute("open") // Close popup
            }, 500)
          })
        }

        // Close quickview
        const closeTriggers = [this.closeModalButton, this.backgroundOverlay].filter(Boolean)

        closeTriggers.forEach(ele => {
          ele.addEventListener("click", () => {
            this.quickviewModalWrapper.classList.remove("active")
            this.backgroundOverlay.classList.add("hidden")
            document.body.classList.remove("overflow-hidden")
          })
        })

      }

      // Show overlay, Quickview and prevent scrolling...
      this.quickviewModalWrapper?.classList.add("active")
      document.body.classList.add("overflow-hidden")
      this.backgroundOverlay?.classList.remove("hidden")

      // Hide Buy now button
      const form = this.quickviewModal?.querySelector(".default_form")
      if (form) form.classList.remove("tw-hidden")

    } catch (err) {
      console.error("QuickView renderSection error:", err)
    }
  }
}

customElements.define("quick-view", QuickView)

// PINCODE CHECKER
class PincodeChecker extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector("input")
    this.checkPincode = this.querySelector("#check-pincode")
    this.changePincode = this.querySelector("#change-pincode")
    this.response = this.querySelector(".response")
    this.successIcon = this.querySelector(".icon-success")
    this.loadingSpinner = this.querySelector(".loading__spinner")
    this.url = this.dataset.url || "" // Pincode serviceability URL from Backend
    this.regex = /^[0-9]{6}$/
    this.pickupPincode = this.dataset.pickupPincode || ""
    this.bearerToken = this.dataset.bearerToken || ""

    this.input.addEventListener("input", () => {
      if (!this.regex.test(this.input.value)) {
        // remove anything not a digit, then trim to max 6 digits
        this.input.value = this.input.value.replace(/\D/g, "").slice(0, 6);
      }
    })

    this.checkPincode?.addEventListener("click", () => this.isValidPincode())
    this.changePincode?.addEventListener("click", () => this.clearAllFields())
  }

  isValidPincode() {
    const pincode = this.input.value

    this.checkPincode.classList.add("loading")
    this.toggleHiddenClass(this.loadingSpinner, false)
    this.toggleHiddenClass(this.response, true)

    if (pincode != "") {
      // this.fetchPincodeDetails(pincode)
      this.fetchPincodeDetailsShipDelight(pincode)
    } else {
      this.response.textContent = "Please enter pincode"

      this.checkPincode.classList.remove("loading")
      this.toggleHiddenClass(this.response, false)
      this.toggleHiddenClass(this.loadingSpinner, true)
    }
  }

  clearAllFields() {
    this.input.value = ""
    this.input.disabled = false

    this.toggleHiddenClass(this.successIcon, true)
    this.toggleHiddenClass(this.response, true)
    this.toggleHiddenClass(this.checkPincode, false)
    this.toggleHiddenClass(this.changePincode, true)
  }

  toggleHiddenClass(ele, value) {
    if (ele) ele.classList.toggle("hidden", value)
  }

  // Use this funciton when it is Delhivery's Pincode serviceablilit API
  async fetchPincodeDetails(pincode) {
    this.response.textContent = "" // Clear the response content
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    const formattedTomorrow = tomorrow.toISOString().split('T')[0]
    let formattedURL = this.url

    if (this.url) {
      formattedURL = `${this.url}?pickup=122003&delivery=${pincode}&mot=S&pdt=B2C&expected_pickup_date=${formattedTomorrow}`
    }

    try {
      const response = await fetch(formattedURL)
      const contentType = response.headers.get("content-type")

      let data = ""
      if (contentType.includes("application/json")) data = await response.json()

      if (data && data.status === 200) {

        if (data.data.serviceable) {
          const expectedDelivery = data.data.details.expected_delivery_date
          const [y, m, d] = expectedDelivery.split("-")

          this.response.textContent = `Expected delivery by ${d}-${m}-${y}`

          // The commented code below hides the “Check” button, disables inputs, and shows a success icon.
          // It also provides a “Change” button to clear the fields after success.
          // Keep for future use if needed.

          /* this.input.disabled = true // Disable the input from changing
            this.toggleHiddenClass(this.successIcon, false)
            this.toggleHiddenClass(this.changePincode, false)
            this.toggleHiddenClass(this.checkPincode, true) */
        } else {
          this.response.textContent = data.data.message
        }
      } else {
        this.response.textContent = data ? data.message : "Data not found"
      }
    } catch (err) {
      this.response.textContent = err.message
    } finally {
      this.checkPincode.classList.remove("loading")
      this.toggleHiddenClass(this.response, false)
      this.toggleHiddenClass(this.loadingSpinner, true)
    }
  }

  // Use this funciton when it is Ship delight's Pincode serviceablilit API
  async fetchPincodeDetailsShipDelight(pincode) {
    this.response.textContent = "" // Clear the response content

    const today = new Date()
    const estimatedDate = new Date(today)

    const dataToSend = {
      pickup_pincode: this.pickupPincode,
      delivery_pincode: pincode,
      pay_type: "COD",
      service_type: "F",
      courier_service_type: "surface"
    }

    try {
      if (pincode.length < 6) {
        this.response.textContent = "Pincode must be 6 digit"
        return
      }

      const response = await fetch(this.url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.bearerToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToSend)
      })

      let data = await response.json()
      data = data.data

      if (data) {

        if (data.response) {
          const isServiceable = data.response.pickup_pincode_serviceable
          const isDeliverable = data.response.delivery_pincode_serviceable
          const EDD = data.response.edd

          estimatedDate.setDate(today.getDate() + 1)  
          const formattedDate = estimatedDate.toISOString().split('T')[0]

          // Estimated full date will be used in the future
          // const [y, m, d] = formattedDate.split("-")
          // this.response.textContent = `Expected delivery by ${d}-${m}-${y}`

          this.response.textContent = !isDeliverable ? "Entered Pincode is not serviceable" : !isServiceable ? "Pickup Pincode is not serviceable" : `Expected delivery in ${EDD} days`

        } else {
          this.response.textContent = "Response not found"
        }
      } else {
        this.response.textContent = "Data not found"
      }
    } catch (err) {
      this.response.textContent = "Invalid response, please try after sometime."
    } finally {
      this.checkPincode.classList.remove("loading")
      this.toggleHiddenClass(this.response, false)
      this.toggleHiddenClass(this.loadingSpinner, true)
    }
  }

}

customElements.define("pincode-checker", PincodeChecker)


// Adjusting cart item height to adapt to different screen sizes
// ensuring proper scrolling without hiding any items

document.addEventListener("DOMContentLoaded", () => {
  const cartItems = document.querySelector("cart-drawer-items");
  const cartDrawerFooter = document.querySelector(".drawer__footer");

  if (cartItems && cartDrawerFooter) {
    const cartItemsFromTop = cartItems.getBoundingClientRect().top;
    const cartDrawerFooterFromTop = cartDrawerFooter.getBoundingClientRect().top;
    const calculatedSpace = `${cartDrawerFooterFromTop - cartItemsFromTop}px` || "150px";

    cartItems.style.setProperty("--cart-items-height", calculatedSpace);
  }
});


// Product card slider
class MediaSliderButton extends HTMLElement {
  constructor() {
    super()

    const selc = {
      prev: "#media-slider-button-prev",
      next: "#media-slider-button-next",
      card: ".card__inner",
      slider: "#slider-media"
    }

    this.prevButton = this.querySelector(selc.prev)
    this.nextButton = this.querySelector(selc.next)
    this.currentSlide = 0

    this.card = this.closest(selc.card)
    this.cardMedia = this.card ? this.card.querySelector(".card__media") : null
    this.mediaSlider = this.card ? this.card.querySelector(selc.slider) : null

    // Initialize functions
    this.initFunc()
  }

  initFunc() {
    if (this.mediaSlider && this.prevButton && this.nextButton) {
      this.mediaCount = parseInt(this.mediaSlider.dataset.mediaCount, 10) || 1
      this.mediaSliderWidth = this.mediaSlider.offsetWidth

      this.prevButton.addEventListener("click", () => this.moveToPrevSlide())
      this.nextButton.addEventListener("click", () => this.moveToNextSlide())

      let timeout;

      this.mediaSlider.addEventListener("scroll", (e) => {

          clearTimeout(timeout) // Update the slide and progress only once
  
          timeout = setTimeout(() => {
            const scrolledWidth = Math.floor(e.target.scrollLeft) || 0
            this.currentSlide = Math.round(scrolledWidth / this.mediaSliderWidth) || 0
            this.updateProgress()
          }, 100)
      }, { passive: true })

      this.updateSlide()
      this.updateProgress()

      this.autoUpdateCount = 1

      this.autoUpdate = setInterval(() => {
        this.autoUpdateInitially(this.autoUpdateCount >= this.mediaCount ? "left" : "right")
      }, 2000)
    }
  }
  moveToPrevSlide() {
    if (this.currentSlide > 0) this.currentSlide--
    this.updateSlide()
    this.updateProgress()
  }
  moveToNextSlide() {
    if (this.currentSlide < (this.mediaCount - 1)) this.currentSlide++

    this.updateSlide()
    this.updateProgress()
  }
  updateSlide() {
    this.mediaSlider.scrollLeft = this.mediaSliderWidth * this.currentSlide
  }
  updateProgress() {
    this.barProgress = (100 / this.mediaCount) * (this.currentSlide + 1)
    if (this.cardMedia) this.cardMedia.style.setProperty('--slider-progress', `${this.barProgress}%`)
  }
  
  
  autoUpdateInitially(towards) {
    if (towards === "right") {
      this.moveToNextSlide()
    } else {
      this.moveToPrevSlide()
    }

    this.autoUpdateCount++

    if (this.autoUpdateCount >= (this.mediaCount * 2)) {
      clearInterval(this.autoUpdate)
    }
  }
}

customElements.define("media-slider-buttons", MediaSliderButton)